import React, {useState, useEffect} from "react";
import {useForm} from "react-hook-form";
import {FaCheck} from "react-icons/fa";
import {useMutation} from "@apollo/client";
import {LoginButton} from "../../styles/Styles";
import {Languages} from "../common/languages";
import {ADD_GUIDE, GET_GUIDES_LIST, UPDATE_GUIDE} from "../gql/guides-gql";
import {toast} from "react-toastify";
import styled from "styled-components";

const FormContainer = styled.div`
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #4CAF50;
    }
`;

const ErrorMessage = styled.span`
    color: #f44336;
    font-size: 0.9rem;
    margin-top: 0.5rem;
    display: block;
`;

const FormTitle = styled.h2`
    margin-bottom: 2rem;
    color: #333;
    text-align: center;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
`;

const LanguagesList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.5rem;
    margin-top: 0.5rem;
`;

const LanguageOption = styled.button<{ $selected: boolean }>`
    padding: 0.8rem;
    border: 1px solid ${props => props.$selected ? '#4CAF50' : '#ddd'};
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${props => props.$selected ? '#e8f5e9' : 'white'};
    font: inherit;
    color: inherit;

    &:hover {
        border-color: #4CAF50;
    }
`;

const PasswordInput = styled(Input)`
    &[type="password"] {
        letter-spacing: 0.3em;
    }
`;

type Inputs = {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
}

interface GuideFormProps {
    guideToEdit?: {
        _id: string;
        email: string;
        profile: {
            firstName: string;
            lastName: string;
            languages: string[];
        };
    };
    onClose?: () => void;
}

const GuideForm: React.FC<GuideFormProps> = ({ guideToEdit, onClose }) => {
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>([Languages.ENGLISH]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<Inputs>();

    const LanguagesArray = Object.values(Languages);

    useEffect(() => {
        if (guideToEdit) {
            setValue('email', guideToEdit.email);
            setValue('firstName', guideToEdit.profile.firstName);
            setValue('lastName', guideToEdit.profile.lastName);
            setSelectedLanguages(guideToEdit.profile.languages);
        } else {
            setSelectedLanguages([Languages.ENGLISH]);
        }
    }, [guideToEdit, setValue]);

    const toggleLanguage = (lang: string) => {
        setSelectedLanguages(prev => {
            if (prev.includes(lang)) {
                return prev.filter(l => l !== lang);
            } else {
                return [...prev, lang];
            }
        });
    };

    const [addGuideMutation] = useMutation(ADD_GUIDE, {
        refetchQueries: [{ query: GET_GUIDES_LIST }],
        onCompleted: () => {
            toast.success("Added successfully!");
            reset();
            setSelectedLanguages([Languages.ENGLISH]);
            onClose?.();
        }
    });

    const [updateGuideMutation] = useMutation(UPDATE_GUIDE, {
        refetchQueries: [{ query: GET_GUIDES_LIST }],
        onCompleted: () => {
            toast.success("Updated successfully!");
            onClose?.();
        }
    });

    const onSubmit = async(data: Inputs) => {
        try {
            const guideInput = {
                email: data.email.trim(),
                firstName: data.firstName,
                lastName: data.lastName,
                languages: selectedLanguages
            };

            if (guideToEdit) {
                await updateGuideMutation({
                    variables: {
                        guideInput: {
                            ...guideInput,
                            _id: guideToEdit._id
                        }
                    }
                });
            } else {
                await addGuideMutation({
                    variables: {
                        guideInput: {
                            ...guideInput,
                            password: data.password
                        }
                    }
                });
            }
        } catch (error) {
            console.error('Error saving guide:', error);
            toast.error('Failed to save guide');
        }
    }

    return (
        <FormContainer>
            <FormTitle>{guideToEdit ? 'Edit Guide' : 'Add New Guide'}</FormTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                        {...register("email", {
                            required: true,
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address"
                            }
                        })}
                        type="email"
                        placeholder="Enter email"
                        defaultValue={guideToEdit?.email || ''}
                    />
                    {errors.email && <ErrorMessage>
                        {errors.email.type === "required"
                            ? "This field is required"
                            : errors.email.message}
                    </ErrorMessage>}
                </FormGroup>

                {!guideToEdit && (
                    <FormGroup>
                        <Label htmlFor="password">Password</Label>
                        <PasswordInput
                            {...register("password", {
                                required: true,
                                minLength: {
                                    value: 6,
                                    message: "Password must be at least 6 characters"
                                }
                            })}
                            type="password"
                            placeholder="Enter password"
                        />
                        {errors.password && <ErrorMessage>
                            {errors.password.type === "required"
                                ? "This field is required"
                                : errors.password.message}
                        </ErrorMessage>}
                    </FormGroup>
                )}

                <FormGroup>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                        {...register("firstName", { required: true })}
                        type="text"
                        placeholder="Enter first name"
                    />
                    {errors.firstName && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                        {...register("lastName", { required: true })}
                        type="text"
                        placeholder="Enter last name"
                    />
                    {errors.lastName && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label>Languages</Label>
                    <LanguagesList>
                        {LanguagesArray.map((lang) => (
                            <LanguageOption
                                key={lang}
                                type="button"
                                $selected={selectedLanguages.includes(lang)}
                                onClick={() => toggleLanguage(lang)}
                            >
                                {lang}
                                {selectedLanguages.includes(lang) && <FaCheck color="#4CAF50" />}
                            </LanguageOption>
                        ))}
                    </LanguagesList>
                </FormGroup>

                <ButtonContainer>
                    <LoginButton type="submit">{guideToEdit ? 'Update Guide' : 'Add Guide'}</LoginButton>
                </ButtonContainer>
            </form>
        </FormContainer>
    );
}

export default GuideForm;
